import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import BarcodeScanner from "@/components/BarcodeScanner";
import PhotoCapture from "@/components/PhotoCapture";
import OrderShareBar from "@/components/OrderShareBar";
import Autocomplete from "@/components/Autocomplete";
import { ScanLine, Camera, Plus, Trash2, ChevronRight, ChevronLeft, Check } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["CUSTOMER", "ITEMS", "REVIEW"];
const UNITS = ["PCS", "SETS"];

export default function NewOrder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [customer, setCustomer] = useState({ name: "", city: "", phone: "", agent: "", agent_phone: "" });
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ design_number: "", quantity: "", unit: "PCS", rate: "", photo_path: "", photo_blob: null });
  const [remarks, setRemarks] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [showPhoto, setShowPhoto] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null);
  const [adminPhone, setAdminPhone] = useState("");

  useEffect(() => {
    api.get("/config/admin-phone").then((r) => setAdminPhone(r.data.admin_phone)).catch(() => {});
  }, []);

  const fetchCustomers = async (q) => {
    const { data } = await api.get("/customers", { params: q ? { q } : {} });
    return (data || []).map((c) => ({
      label: c.name,
      meta: [c.phone, c.city, c.agent].filter(Boolean).join(" · "),
      raw: c,
    }));
  };
  const fetchAgents = async (q) => {
    const { data } = await api.get("/agents", { params: q ? { q } : {} });
    return (data || []).map((a) => ({ label: a.name, meta: a.phone || "", raw: a }));
  };

  const canNext = () => {
    if (step === 0) return customer.name && customer.city && customer.phone && customer.agent;
    if (step === 1) return items.length > 0;
    return true;
  };

  const addItem = async () => {
    if (!draft.design_number || !draft.quantity || !draft.rate) {
      toast.error("Fill design number, quantity & rate");
      return;
    }
    let photo_path = "";
    if (draft.photo_blob) {
      try {
        const fd = new FormData();
        fd.append("file", draft.photo_blob, `photo-${Date.now()}.jpg`);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_path = data.path;
      } catch (e) {
        toast.error("Photo upload failed");
      }
    }
    setItems([...items, {
      design_number: draft.design_number.trim(),
      quantity: parseInt(draft.quantity, 10),
      unit: (draft.unit || "PCS").toUpperCase(),
      rate: parseFloat(draft.rate),
      photo_path,
    }]);
    setDraft({ design_number: "", quantity: "", unit: draft.unit, rate: "", photo_path: "", photo_blob: null });
  };

  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);
  const totalPcs = items.filter((i) => (i.unit || "PCS") === "PCS").reduce((s, i) => s + i.quantity, 0);
  const totalSets = items.filter((i) => (i.unit || "PCS") === "SETS").reduce((s, i) => s + i.quantity, 0);
  const totalAmt = items.reduce((s, i) => s + i.quantity * i.rate, 0);
  const gstAmt = Math.round(totalAmt * 0.05 * 100) / 100;
  const grandTotal = Math.round((totalAmt + gstAmt) * 100) / 100;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        customer,
        items,
        remarks,
      });
      setCreated(data);
      toast.success(`Order ${data.order_no} created`);
    } catch (e) {
      toast.error("Failed to create order");
    } finally {
      setSubmitting(false);
    }
  };

  // === SUCCESS SCREEN ===
  if (created) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="order-success">
        <div className="brut-card p-8 text-center">
          <div className="w-16 h-16 bg-[#2A9D8F] border-2 border-black mx-auto mb-4 flex items-center justify-center brut-shadow">
            <Check className="text-white" strokeWidth={3} size={32} />
          </div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#2A9D8F] font-bold mb-2">ORDER LOGGED</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter mb-2">{created.order_no}</h1>
          <div className="text-sm text-[#666] uppercase tracking-wider mb-6">
            {created.total_quantity} pcs · Subtotal ₹{Math.round(created.total_amount).toLocaleString("en-IN")} · GST ₹{Math.round(created.gst_amount || 0).toLocaleString("en-IN")} · <b className="text-[#0A0A0A]">Total ₹{Math.round(created.grand_total || created.total_amount).toLocaleString("en-IN")}</b>
          </div>
          <OrderShareBar order={created} adminPhone={adminPhone} />
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            <button onClick={() => navigate(`/orders/${created.id}`)} className="brut-btn brut-btn-secondary" data-testid="view-order-btn">
              VIEW ORDER
            </button>
            <button onClick={() => { setCreated(null); setStep(0); setCustomer({name:"",city:"",phone:"",agent:"",agent_phone:""}); setItems([]); setRemarks(""); }}
                    className="brut-btn brut-btn-secondary" data-testid="new-another-btn">
              CREATE ANOTHER
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto" data-testid="new-order-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-[#E63946] font-bold mb-2">── ORDER WIZARD</div>
        <h1 className="font-heading font-black text-4xl tracking-tighter">NEW ORDER.</h1>
      </div>

      {/* Stepper */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {STEPS.map((s, i) => (
          <div key={s} className={`border-2 border-black p-3 text-center text-xs uppercase tracking-wider font-bold
            ${i === step ? "bg-[#0A0A0A] text-white" : i < step ? "bg-[#2A9D8F] text-white" : "bg-white"}`}
            data-testid={`step-${i}`}>
            {String(i + 1).padStart(2, "0")} · {s}
          </div>
        ))}
      </div>

      {/* STEP 0: Customer */}
      {step === 0 && (
        <div className="brut-card p-6 space-y-4" data-testid="step-customer">
          <div>
            <label className="label-mono">Customer Name</label>
            <Autocomplete
              value={customer.name}
              onChange={(v) => setCustomer({ ...customer, name: v })}
              fetcher={fetchCustomers}
              placeholder="Type to search saved customers…"
              testid="input-customer-name"
              onSelect={(item) => {
                const c = item.raw || {};
                setCustomer({
                  name: c.name || customer.name,
                  city: c.city || customer.city,
                  phone: c.phone || customer.phone,
                  agent: c.agent || customer.agent,
                  agent_phone: customer.agent_phone,
                });
              }}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-mono">City</label>
              <input className="brut-input" data-testid="input-customer-city"
                     value={customer.city} onChange={(e) => setCustomer({ ...customer, city: e.target.value })} />
            </div>
            <div>
              <label className="label-mono">Phone (10-digit)</label>
              <input className="brut-input" data-testid="input-customer-phone" inputMode="numeric"
                     value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label-mono">Agent</label>
              <Autocomplete
                value={customer.agent}
                onChange={(v) => setCustomer({ ...customer, agent: v })}
                fetcher={fetchAgents}
                placeholder="Type to search agents…"
                testid="input-customer-agent"
                onSelect={(item) => {
                  const a = item.raw || {};
                  setCustomer({ ...customer, agent: a.name || customer.agent, agent_phone: a.phone || customer.agent_phone });
                }}
              />
            </div>
            <div>
              <label className="label-mono">Agent Phone</label>
              <input
                className="brut-input"
                data-testid="input-agent-phone"
                inputMode="numeric"
                placeholder="10-digit mobile"
                value={customer.agent_phone}
                onChange={(e) => setCustomer({ ...customer, agent_phone: e.target.value })}
              />
            </div>
          </div>
          <div className="text-xs text-[#666] uppercase tracking-wider">
            ▸ New names auto-save to your masters on order confirm.
          </div>
        </div>
      )}

      {/* STEP 1: Items */}
      {step === 1 && (
        <div className="space-y-4" data-testid="step-items">
          <div className="brut-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-black text-xl">ADD ITEM</div>
              <button onClick={() => setShowScanner(true)} className="brut-btn brut-btn-primary !py-2 !px-3 text-xs" data-testid="open-scanner-btn">
                <ScanLine size={14} strokeWidth={3} /> SCAN
              </button>
            </div>
            <div className="grid sm:grid-cols-4 gap-3 mb-3">
              <div>
                <label className="label-mono">Design #</label>
                <input className="brut-input" data-testid="input-design-no"
                       value={draft.design_number}
                       onChange={(e) => setDraft({ ...draft, design_number: e.target.value })} />
              </div>
              <div>
                <label className="label-mono">Quantity</label>
                <input className="brut-input" data-testid="input-qty" inputMode="numeric"
                       value={draft.quantity}
                       onChange={(e) => setDraft({ ...draft, quantity: e.target.value })} />
              </div>
              <div>
                <label className="label-mono">Unit</label>
                <div className="grid grid-cols-2 border-2 border-black" data-testid="unit-toggle">
                  {UNITS.map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setDraft({ ...draft, unit: u })}
                      data-testid={`unit-${u}`}
                      className={`py-3 font-bold uppercase tracking-wider text-sm
                        ${draft.unit === u ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#F4F4F0]"}`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="label-mono">Rate (₹)</label>
                <input className="brut-input" data-testid="input-rate" inputMode="decimal"
                       value={draft.rate}
                       onChange={(e) => setDraft({ ...draft, rate: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button onClick={() => setShowPhoto(true)} className="brut-btn brut-btn-secondary !py-2 text-xs" data-testid="open-camera-btn">
                <Camera size={14} strokeWidth={3} /> {draft.photo_blob ? "PHOTO ATTACHED" : "ADD LIVE PHOTO"}
              </button>
              {draft.photo_blob && (
                <img src={URL.createObjectURL(draft.photo_blob)} alt="" className="w-12 h-12 border-2 border-black object-cover" />
              )}
              <button onClick={addItem} className="brut-btn brut-btn-dark !py-2 text-xs ml-auto" data-testid="add-item-btn">
                <Plus size={14} strokeWidth={3} /> ADD TO ORDER
              </button>
            </div>
          </div>

          <div className="brut-card overflow-hidden">
            <div className="px-5 py-3 border-b-2 border-black flex items-center justify-between">
              <div className="font-heading font-black text-lg">ITEMS ({items.length})</div>
              <div className="text-xs uppercase tracking-wider">
                <span className="mr-3">PCS: <b>{totalPcs}</b></span>
                <span className="mr-3">SETS: <b>{totalSets}</b></span>
                <span className="mr-3">Sub ₹<b>{Math.round(totalAmt).toLocaleString("en-IN")}</b></span>
                <span>+GST ₹<b>{Math.round(grandTotal).toLocaleString("en-IN")}</b></span>
              </div>
            </div>
            {items.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#666] uppercase tracking-wider">No items yet. Scan or enter manually.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-[#F4F4F0] border-b-2 border-black">
                  <tr className="text-left">
                    <th className="px-4 py-2 text-xs uppercase">#</th>
                    <th className="px-4 py-2 text-xs uppercase">Design</th>
                    <th className="px-4 py-2 text-xs uppercase">Qty</th>
                    <th className="px-4 py-2 text-xs uppercase">Unit</th>
                    <th className="px-4 py-2 text-xs uppercase">Rate</th>
                    <th className="px-4 py-2 text-xs uppercase">Amount</th>
                    <th className="px-4 py-2 text-xs uppercase">Photo</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => (
                    <tr key={idx} className="border-b border-black/15" data-testid={`item-row-${idx}`}>
                      <td className="px-4 py-2 font-mono">{idx + 1}</td>
                      <td className="px-4 py-2 font-mono font-bold">{it.design_number}</td>
                      <td className="px-4 py-2 font-mono">{it.quantity}</td>
                      <td className="px-4 py-2 font-mono">{it.unit || "PCS"}</td>
                      <td className="px-4 py-2 font-mono">₹{it.rate}</td>
                      <td className="px-4 py-2 font-mono font-bold">₹{it.quantity * it.rate}</td>
                      <td className="px-4 py-2">{it.photo_path ? "✓" : "—"}</td>
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => removeItem(idx)} data-testid={`remove-item-${idx}`} className="text-[#E63946]">
                          <Trash2 size={16} strokeWidth={3} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* STEP 2: Review */}
      {step === 2 && (
        <div className="space-y-4" data-testid="step-review">
          <div className="brut-card p-6">
            <div className="font-heading font-black text-xl mb-3">CUSTOMER</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm font-mono">
              <div><span className="label-mono !inline-block !mb-0">Name</span><div>{customer.name}</div></div>
              <div><span className="label-mono !inline-block !mb-0">City</span><div>{customer.city}</div></div>
              <div><span className="label-mono !inline-block !mb-0">Phone</span><div>{customer.phone}</div></div>
              <div><span className="label-mono !inline-block !mb-0">Agent</span><div>{customer.agent}</div></div>
            </div>
          </div>

          <div className="brut-card p-6">
            <div className="font-heading font-black text-xl mb-3">ITEMS</div>
            <table className="w-full text-sm">
              <thead className="border-b-2 border-black"><tr className="text-left text-xs uppercase">
                <th className="py-2">Design</th><th className="py-2">Qty</th><th className="py-2">Unit</th><th className="py-2">Rate</th><th className="py-2">Amt</th>
              </tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-b border-black/15">
                    <td className="py-2 font-bold">{it.design_number}</td>
                    <td className="py-2">{it.quantity}</td>
                    <td className="py-2">{it.unit || "PCS"}</td>
                    <td className="py-2">₹{it.rate}</td>
                    <td className="py-2">₹{it.quantity * it.rate}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-black">
                <tr className="font-mono"><td className="py-2" colSpan="4">Subtotal</td><td className="py-2">₹{Math.round(totalAmt).toLocaleString("en-IN")}</td></tr>
                <tr className="font-mono"><td className="py-2" colSpan="4">GST @ 5%</td><td className="py-2">₹{Math.round(gstAmt).toLocaleString("en-IN")}</td></tr>
                <tr className="font-heading font-black">
                  <td className="py-2">GRAND TOTAL</td>
                  <td colSpan="3" className="py-2 text-xs">{totalPcs ? `${totalPcs} PCS` : ""}{totalPcs && totalSets ? " + " : ""}{totalSets ? `${totalSets} SETS` : ""}</td>
                  <td className="py-2">₹{Math.round(grandTotal).toLocaleString("en-IN")}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="brut-card p-6">
            <label className="label-mono">Remarks / Specifications</label>
            <textarea className="brut-input min-h-[100px]" data-testid="input-remarks"
                      value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="e.g. Pack separately by colour, urgent dispatch…" />
          </div>
        </div>
      )}

      {/* Nav */}
      <div className="flex items-center justify-between mt-6">
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
                className="brut-btn brut-btn-secondary" data-testid="prev-btn">
          <ChevronLeft size={16} strokeWidth={3} /> BACK
        </button>
        {step < 2 ? (
          <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()}
                  className="brut-btn brut-btn-primary" data-testid="next-btn">
            NEXT <ChevronRight size={16} strokeWidth={3} />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} className="brut-btn brut-btn-primary" data-testid="submit-order-btn">
            {submitting ? "SAVING…" : "CONFIRM ORDER"} <Check size={16} strokeWidth={3} />
          </button>
        )}
      </div>

      {showScanner && <BarcodeScanner
        onResult={(code) => { setDraft({ ...draft, design_number: code }); setShowScanner(false); toast.success(`Scanned: ${code}`); }}
        onClose={() => setShowScanner(false)} />}
      {showPhoto && <PhotoCapture
        onCapture={(blob) => { setDraft({ ...draft, photo_blob: blob }); setShowPhoto(false); }}
        onClose={() => setShowPhoto(false)} />}
    </div>
  );
}
